import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ClientInfoConfig, ClientStudy, FilesWithIdentifiers, configuration_data } from '../models/configurations.model';
import { results } from '../models/common.model';
import { environment } from 'src/app/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private apiUrl = environment.baseApiUrl + "api/Configuration/";

  private MN_DefaultConfiguration: string = "getDefaultConfiguration";
  private MN_GetConfiguration: string = "getConfiguration";
  private MN_InsertUpdateConfiguration: string = "insertUpdateConfiguration";
  private MN_SaveClientTabData: string = "SaveClientTabData";
  private MN_ModuleName: string = "getModuleName";
  private MN_GetSavedModules: string = "getSavedModules";
  private MN_DefaultColorPaletteFile: string = "getDefaultColorPaletteFile";
  private MN_ExistingColorPaletteFile: string = "downloadExistingColors";
  private MN_GetColorPaletteFileName: string = "GetColorPaletteFileName";
  private MN_ExcelDataTemplateFile: string = "getQuestionMappingExcelDataTemplateFile";
  private MN_ExistingExcelDataFile: string = "downloadExistingQuestionMappingExcelData";
  private MN_UploadExcelDataFile: string = "uploadQuestionMappingExcelDataFile";

  private MN_GetLogo: string = "getLogo";
  private MN_UploadThemeToAzure: string = "uploadThemeToAzure";

  constructor(private http: HttpClient) { }

  /**
   * Get the default configuration details
   * @returns {Observable<results>}
   */
  getDefaultConfigurations(moduleName?: string): Observable<results> {
    return this.http.get<any>(`${this.apiUrl}${this.MN_DefaultConfiguration}`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError((error: any) => new Error(error));
      })
    );
  }
  getColorPaletteFile(): Observable<results> {
    return this.http.get<any>(`${this.apiUrl}${this.MN_DefaultColorPaletteFile}`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError((error: any) => new Error(error));
      })
    );
  }
  getModuleName(): Observable<results> {
    return this.http.get<any>(`${this.apiUrl}${this.MN_ModuleName}`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError((error: any) => new Error(error));
      })
    );
  }
  SaveClientTabData(data: any, logo: File, selectedClientName: string, colorPaletteList: string[], colorFile: File, additionalFeatures: any, customReportExcelColors: any): Observable<results> {
    //front-end:
    let formData: FormData = new FormData();

    formData.append('clientname', selectedClientName);
    formData.append('File', logo);
    formData.append('colorFile', colorFile);
    formData.append('jsonString', JSON.stringify(data));
    formData.append('colorPaletteList', JSON.stringify(colorPaletteList));
    formData.append('additionalFeatures', JSON.stringify(additionalFeatures));
    formData.append('customReportExcelColors', JSON.stringify(customReportExcelColors));

    const body = {
      data: data
    };

    return this.http.post<any>(`${this.apiUrl}${this.MN_SaveClientTabData}`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError(() => new Error('test'));
      })
    );
  }
  /**
   * Get configuration details based on client name and study name 
   * @param {ClientStudy} data - .client name and study name object
   * @returns {Observable<results>}
   */
  getClientStudyData(data: ClientStudy): Observable<results> {
    return this.http.post<any>(`${this.apiUrl}${this.MN_GetConfiguration}`, data).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError((error: any) => new Error(error));
      })
    );
  }
  savedModule(value: string): Observable<results> {

    const body = {
      client_name: value
    }
    return this.http.post<any>(`${this.apiUrl}${this.MN_GetSavedModules}`, body).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError((error: any) => new Error(error));
      })
    );
  }
  getLogo(value: string): Observable<results> {

    const body = {
      client_name: value
    }
    return this.http.post<any>(`${this.apiUrl}${this.MN_GetLogo}`, body).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError((error: any) => new Error(error));
      })
    );
  }

  getLogoFiles(clientName: string): Observable<FilesWithIdentifiers[]> {
    const body = { client_name: clientName };
    return this.http.post<FilesWithIdentifiers[]>(`${this.apiUrl}GetLogoFiles`, body).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError((error: any) => new Error(error));
      })
    );
  }

  getColorPaletteFileName(clientName: string): Observable<any> {
    return this.http.get(`${this.apiUrl}${this.MN_GetColorPaletteFileName}?client_name=${encodeURIComponent(clientName)}`, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError((error: any) => new Error(error));
      })
    );
  }

  /**
   *  insert / update configuration data
   * @param data - .configuration data
   * @returns {Observable<results>}
   */
  insertUpdateConfigurations(data: ClientInfoConfig, logo: File, listFiles: FilesWithIdentifiers[]): Observable<results> {
    //front-end:
    let formData: FormData = new FormData();
    formData.append('File', logo);
    for (let i = 0; i < listFiles.length; i++) {
      formData.append('DocFile', listFiles[i].file!);
      formData.append('foldersName', listFiles[i].identifier);
    }
    formData.append('jsonString', JSON.stringify(data));

    return this.http.post<any>(`${this.apiUrl}${this.MN_InsertUpdateConfiguration}`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        // You can handle the error here and throw a custom error if needed
        return throwError(() => new Error('test'));
      })
    );
  }
  downloadExistingColors(clientName: string): Observable<Blob> {
    const body = {
      client_name: clientName
    }
    const exportUrl = `${this.apiUrl}${this.MN_ExistingColorPaletteFile}`;
    return this.http.post(exportUrl, body, { responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(error);
      })
    );
  }

  /**
   * Get Excel Data Template file
   * @returns {Observable<results>}
   */
  getExcelDataTemplateFile(): Observable<results> {
    return this.http.get<any>(`${this.apiUrl}${this.MN_ExcelDataTemplateFile}`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError((error: any) => new Error(error));
      })
    );
  }

  /**
   * Download existing Excel Data file for a client
   * @param {string} clientName - Client name
   * @returns {Observable<Blob>}
   */
  downloadExistingExcelData(clientName: string): Observable<Blob> {
    const body = {
      client_name: clientName
    }
    const exportUrl = `${this.apiUrl}${this.MN_ExistingExcelDataFile}`;
    return this.http.post(exportUrl, body, { responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(error);
      })
    );
  }

  /**
   * Upload Excel Data file
   * @param {File} excelFile - Excel file to upload
   * @param {string} clientName - Client name
   * @returns {Observable<results>}
   */
  uploadExcelDataFile(excelFile: File, clientName: string): Observable<results> {
    let formData: FormData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('clientName', clientName);

    return this.http.post<any>(`${this.apiUrl}${this.MN_UploadExcelDataFile}`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error uploading Excel data file'));
      })
    );
  }

  downloadFile(data: Blob, filename: string) {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;

    // Trigger download
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private MN_GetCacheEntries: string = "listInsightsCache";
  private MN_DeleteCacheEntry: string = "deleteInsightsCache";

  getCacheEntries(pageSize: number = 100, from: number = 0): Observable<results> {
    let params = new HttpParams()
      .set('size', pageSize.toString())
      .set('from', from.toString());

    return this.http.get<any>(`${this.apiUrl}${this.MN_GetCacheEntries}`, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error fetching cache entries'));
      })
    );
  }

  deleteCacheEntry(cacheKey: string): Observable<results> {
    const payload = {
      cacheKeys: [cacheKey],
      deleteAll: false
    };

    return this.http.post<any>(`${this.apiUrl}${this.MN_DeleteCacheEntry}`, payload).pipe(
      map((response: any) => response),
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error deleting cache entry'));
      })
    );
  }

  /**
   * Upload theme Excel file to Azure and trigger theme updater
   * @param clientName - The client name
   * @param file - The Excel file containing theme variables
   * @returns {Observable<results>}
   */
  uploadThemeToAzure(clientName: string, azureUrl: string, file: File): Observable<results> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);
    formData.append('azureUrl', azureUrl);
    formData.append('file', file);

    return this.http.post(`${this.apiUrl}${this.MN_UploadThemeToAzure}`, formData, { responseType: 'text' }).pipe(
      map((response: string) => {
        // Backend returns JSON string, parse it
        return JSON.parse(response);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error uploading theme to Azure'));
      })
    );
  }

  /**
   * Check if domain.xlsx exists for a client
   */
  checkDomainExcelExists(clientName: string, azureUrl: string): Observable<results> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);
    formData.append('azureUrl', azureUrl);

    return this.http.post<results>(`${this.apiUrl}checkDomainExcelExists`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error checking domain.xlsx'));
      })
    );
  }

  /**
   * Download domain.xlsx file for a client
   */
  downloadDomainExcel(clientName: string): Observable<Blob> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);

    return this.http.post(`${this.apiUrl}downloadDomainExcel`, formData, { responseType: 'blob' });
  }

  /**
   * Download CSS subfolder (icons, snapshot-tiles, landing-video, header-band) as ZIP
   * Backend builds Azure URL internally from configuration
   */
  downloadCssFolder(clientName: string, folderName: string, subFolder?: string): Observable<Blob> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);
    formData.append('folderName', folderName);
    // optional: subfolder under the folderName (e.g., ppt_study_1)
    if (subFolder) {
      formData.append('subFolder', subFolder);
    }

    return this.http.post(`${this.apiUrl}downloadCssFolder`, formData, { responseType: 'blob' });
  }

  /**
   * Rename CSS folder to css_{timestamp} backup
   */
  renameCssFolderToBackup(clientName: string): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);

    return this.http.post(`${this.apiUrl}renameCssFolderToBackup`, formData);
  }

  /**
   * Check if a CSS subfolder exists for a client (icons, snapshot-tiles, landing-video, header-band)
   */
  checkCssSubfolderExists(clientName: string, folderName: string): Observable<any> {
    return this.checkCssSubfolderExistsWithSubfolder(clientName, folderName);
  }

  /**
   * Check if a CSS subfolder exists for a client, optionally inside a nested subFolder
   */
  checkCssSubfolderExistsWithSubfolder(clientName: string, folderName: string, subFolder?: string): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);
    formData.append('folderName', folderName);
    if (subFolder) {
      formData.append('subFolder', subFolder);
    }

    return this.http.post<any>(`${this.apiUrl}checkCssSubfolderExists`, formData).pipe(
      catchError(() => {
        return throwError(() => new Error('Error checking CSS subfolder'));
      })
    );
  }

  /**
   * Upload image files (SVG, PNG, ZIP) to Azure for CSS customization
   * Supports icons, snapshot-tiles, landing-video, header-band
   * Backend builds Azure URL internally from configuration
   */
  uploadImageFilesToAzure(clientName: string, folderType: string, file: File, subFolder?: string): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('clientName', clientName);
    formData.append('folderType', folderType);
    if (subFolder) {
      formData.append('subFolder', subFolder);
    }
    formData.append('file', file);

    return this.http.post<any>(`${this.apiUrl}uploadImageFilesToAzure`, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error uploading image files to Azure'));
      })
    );
  }

  /**
   * Download default templates (following same pattern as downloadDomainExcel)
   * Backend builds the path to env-dev/default-templates
   * @param selectedItems - Array of folder names to download (excel, icons, snapshot-tiles, landing-video, header-band)
   */
  downloadDefaultTemplates(selectedItems: string[]): Observable<Blob> {
    let formData: FormData = new FormData();
    
    // Add each selected item to FormData
    selectedItems.forEach((item, index) => {
      formData.append(`selectedItems[${index}]`, item);
    });

    return this.http.post(`${this.apiUrl}downloadDefaultTemplates`, formData, { responseType: 'blob' });
  }

  /**
   * Get all uploaded filenames for a client in one API call
   */
  getAllUploadedFileNames(clientName: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}getAllUploadedFileNames?clientName=${encodeURIComponent(clientName)}`);
  }


  deleteAllCacheEntries(): Observable<results> {
    const payload = {
      deleteAll: true
    };

    return this.http.post<any>(`${this.apiUrl}${this.MN_DeleteCacheEntry}`, payload).pipe(
      map((response: any) => response),
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error deleting all cache entries'));
      })
    );
  }

  /**
   * Get landing video for a client - proxies through API to avoid CORS issues
   * @param clientName The client name
   * @returns Observable<Blob> The video file as a blob
   */
  getLandingVideo(clientName: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}getLandingVideo/${encodeURIComponent(clientName)}`, {
      responseType: 'blob'
    });
  }

  getDedicatedLandingVideo(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}getDedicatedLandingVideo`, { responseType: 'blob' });
  }

  getDedicatedLandingImage(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}getDedicatedLandingImage`, { responseType: 'blob' });
  }

  getSignificanceTestingDetails(clientName: string, study: string): Observable<results> {
    return this.http.post<results>(`${this.apiUrl}significanceTestingDetails`, { client_name: clientName, study }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error fetching significance testing details'));
      })
    );
  }

  saveSignificanceTesting(payload: any): Observable<results> {
    return this.http.post<results>(`${this.apiUrl}saveSignificanceTesting`, payload).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Request error:', error);
        return throwError(() => new Error('Error saving significance testing'));
      })
    );
  }
}

